import { type ReactNode } from 'react'

import { clsx } from 'clsx'

import type { SelectOption } from './types'

interface MobileBottomSheetProps<T> {
  placeholder: string
  searchPlaceholder: string
  searchQuery: string
  filteredOptions: SelectOption<T>[]
  value: T | null | undefined
  isLoading: boolean
  noOptionsText: string
  resolvedFooterAction?: ReactNode
  searchInputRef: React.Ref<HTMLInputElement>
  onSearchChange: (value: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onSelect: (option: SelectOption<T>) => void
  onClose: () => void
}

const SHEET_CLASS = clsx(
  'absolute bottom-0 left-0 right-0',
  'bg-popover text-popover-foreground',
  'rounded-t-2xl shadow-xl',
  'animate-slide-up',
  'flex flex-col',
  'max-h-[85vh]',
)

function MobileSearchBar({ searchInputRef, searchPlaceholder, searchQuery, onSearchChange, onKeyDown }: {
  searchInputRef: React.Ref<HTMLInputElement>
  searchPlaceholder: string
  searchQuery: string
  onSearchChange: (v: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
}): JSX.Element {
  return (
    <div className="p-3 border-b border-border flex-shrink-0">
      <input
        ref={searchInputRef}
        autoCapitalize="off"
        autoComplete="off"
        autoCorrect="off"
        className="el-input el-input-md bg-secondary"
        placeholder={searchPlaceholder}
        type="text"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
    </div>
  )
}

export function MobileBottomSheet<T>(p: MobileBottomSheetProps<T>): JSX.Element {
  const handleBackdropKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') { p.onClose() }
  }
  return (
    <div className="fixed inset-0 z-50">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-black/50 animate-fade-in"
        role="presentation"
        onClick={p.onClose}
        onKeyDown={handleBackdropKeyDown}
      />
      <div className={SHEET_CLASS} role="listbox" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
          <div className="w-10 h-1 bg-muted-foreground/40 rounded-full" />
        </div>
        <MobileSheetHeader placeholder={p.placeholder} onClose={p.onClose} />
        <MobileSearchBar
          searchInputRef={p.searchInputRef}
          searchPlaceholder={p.searchPlaceholder}
          searchQuery={p.searchQuery}
          onKeyDown={p.onKeyDown}
          onSearchChange={p.onSearchChange}
        />
        <MobileOptionsList
          filteredOptions={p.filteredOptions}
          isLoading={p.isLoading}
          noOptionsText={p.noOptionsText}
          value={p.value}
          onSelect={p.onSelect}
        />
        {p.resolvedFooterAction && (
          <div className="border-t border-border p-4 flex-shrink-0 bg-popover">
            {p.resolvedFooterAction}
          </div>
        )}
        <div className="h-6 flex-shrink-0" />
      </div>
    </div>
  )
}

interface MobileSheetHeaderProps {
  placeholder: string
  onClose: () => void
}

function MobileSheetHeader({ placeholder, onClose }: MobileSheetHeaderProps): JSX.Element {
  return (
    <div className="flex items-center justify-between px-4 pb-3 border-b border-border flex-shrink-0">
      <h2 className="text-lg font-semibold text-foreground">{placeholder}</h2>
      <button
        className="p-2 -mr-2 text-muted-foreground hover:text-foreground"
        type="button"
        onClick={onClose}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
      </button>
    </div>
  )
}

interface MobileOptionsListProps<T> {
  filteredOptions: SelectOption<T>[]
  value: T | null | undefined
  isLoading: boolean
  noOptionsText: string
  onSelect: (option: SelectOption<T>) => void
}

function MobileOptionsList<T>({
  filteredOptions,
  value,
  isLoading,
  noOptionsText,
  onSelect,
}: MobileOptionsListProps<T>): JSX.Element {
  return (
    <div className="flex-1 overflow-y-auto overscroll-contain">
      {isLoading && <MobileLoadingSpinner />}
      {!isLoading && filteredOptions.length === 0 && (
        <div className="px-4 py-8 text-center text-muted-foreground">{noOptionsText}</div>
      )}
      {!isLoading && filteredOptions.length > 0 && (
        filteredOptions.map((option) => (
          <MobileOptionItem
            key={String(option.value)}
            isSelected={option.value === value}
            option={option}
            onSelect={onSelect}
          />
        ))
      )}
    </div>
  )
}

function MobileLoadingSpinner(): JSX.Element {
  return (
    <div className="px-4 py-8 text-center text-muted-foreground">
      <svg className="animate-spin h-6 w-6 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-75"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          fill="currentColor"
        />
      </svg>
      Loading...
    </div>
  )
}

interface MobileOptionItemProps<T> {
  option: SelectOption<T>
  isSelected: boolean
  onSelect: (option: SelectOption<T>) => void
}

function MobileOptionItem<T>({ option, isSelected, onSelect }: MobileOptionItemProps<T>): JSX.Element {
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect(option)
    }
  }

  return (
    <div
      aria-selected={isSelected}
      className={clsx(
        'px-4 py-3 border-b border-border/50',
        'active:bg-accent',
        isSelected && 'bg-accent/50',
        option.disabled && 'opacity-50'
      )}
      role="option"
      tabIndex={0}
      onClick={() => onSelect(option)}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium text-foreground">{option.label}</div>
          {option.description && (
            <div className="text-sm text-muted-foreground">{option.description}</div>
          )}
        </div>
        {isSelected && (
          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              d="M5 13l4 4L19 7"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
        )}
      </div>
    </div>
  )
}
